import { yupResolver } from '@hookform/resolvers/yup';
import { GuestModel, GuestMutationInput } from 'api-hooks/guest';
import { ChevronRight } from 'baseui/icon';
import { CloseSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Tabs from 'components/common/tabs';
import { Form, FormState } from 'components/elements';
import { Button } from 'components/elements/buttons';
import FormHeader from 'components/widgets/form-header';
import useNavigation from 'hooks/use-navigation';
import {
  ButtonContainer,
  EmptyTabStyles,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import ActionComponent from './action-component';
import DetailTab from './detail-tab';
import DocumentTab from './document-tab';
import { GuestFormSchema, GuestFormType } from './form-type';
import { PassportCsvProvider } from './hook/use-passport-csv';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  guest?: GuestModel;
  onSubmit: (
    input: GuestMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<GuestModel | undefined>;
  isLite?: boolean;
}

export default function GuestForm(props: Props) {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = React.useState<React.Key>('0');
  const { guest, isLite = false } = props;
  const { navigate } = useNavigation();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues(guest);

  const methods = useForm<GuestFormType>({
    resolver: yupResolver(GuestFormSchema()),
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: GuestFormType) => {
      try {
        const preferences: GuestMutationInput['preferences'] =
          values.preferences.map((preference) => {
            return preference.id;
          });

        const passport: GuestMutationInput['passport'] = values.passport
          ? {
              ...values.passport,
              id: guest?.passports?.id,
            }
          : null;

        const apecCard: GuestMutationInput['apecCard'] = values.apecCard
          ? {
              ...values.apecCard,
              id: guest?.apecCard?.id,
            }
          : null;

        const result: GuestMutationInput = {
          ...values,
          preferences,
          passport,
          apecCard,
        };
        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [guest, methods, props],
  );

  const onNavigateDashboard = React.useCallback(() => {
    navigate(NavigationRoutes.GuestDashboardView, {
      id: guest!.id,
    });
  }, [guest, navigate]);

  const customViewName = guest && `${t('common:guest')} - ${guest.titledName}`;

  const title = isLite
    ? ''
    : guest
    ? customViewName ?? t('common:guest')
    : t('common:add_extra', { extra: t('common:guest') });

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!guest}
      customViewName={customViewName}
      keyName={asPath}
    >
      <PassportCsvProvider>
        <div
          style={{
            position: 'relative',
          }}
        >
          <FormHeader
            data={guest}
            noDelete
            noEdit
            title={title}
            {...(guest && {
              onRenderComponent: (
                <FormState>
                  {({ editable, setIsEditable }) =>
                    !editable && guest ? (
                      <>
                        <ActionComponent guest={guest} />
                        {activeKey === '0' && (
                          <ButtonContainer $style={{ marginLeft: '16px' }}>
                            <Button
                              onClick={onNavigateDashboard}
                              buttonType="primary"
                              endEnhancer={(color, size) => (
                                <ChevronRight
                                  width={size}
                                  height={size}
                                  color={color}
                                />
                              )}
                              disabled={methods.formState.isSubmitting}
                            >
                              {t('customer:open_dashboard')}
                            </Button>
                          </ButtonContainer>
                        )}
                      </>
                    ) : (
                      <>
                        <ButtonContainer $style={{ marginLeft: '16px' }}>
                          <Button
                            onClick={() => {
                              methods.reset();
                              setIsEditable(false);
                              methods.clearErrors();
                            }}
                            buttonType="secondary"
                            startEnhancer={(color, size) => (
                              <CloseSVG
                                width={size}
                                height={size}
                                color={color}
                              />
                            )}
                            disabled={methods.formState.isSubmitting}
                          >
                            {t('common:cancel')}
                          </Button>
                        </ButtonContainer>
                      </>
                    )
                  }
                </FormState>
              ),
            })}
          />
          <Tabs
            activeKey={activeKey}
            onChangeActive={({ activeKey }) => setActiveKey(activeKey)}
            items={[
              {
                title: t('common:detail'),
                component: <></>,
              },
              { title: t('common:document'), component: <></> },
            ]}
            overrideStyles={EmptyTabStyles}
          />
          <div
            style={{
              maxHeight: 'calc(100dvh - 250px)',
              overflow: 'auto',
            }}
          >
            {activeKey === '0' ? <DetailTab guest={guest} /> : <DocumentTab />}
            <SeparatorVertical $margin={32} />
          </div>
        </div>
      </PassportCsvProvider>
    </Form>
  );
}
